/**
 * Mapbox Directions API client.
 *
 * Why these request parameters matter:
 *  - `banner_instructions` returns the display banners, and crucially their
 *    `sub` banner carries the per-lane arrow data we draw in the lane bar.
 *  - `steps` + `geometries=geojson` gives us `intersections[].lanes`, the
 *    authoritative lane model (which lanes exist, which are valid for the turn).
 *  - `annotations=congestion` colours the route line by live traffic.
 *  - `bearings` biases the first leg to the direction the vehicle is actually
 *    pointing, which stops a reroute from proposing an immediate U-turn.
 *  - `max_height/width/weight` apply the Hino's dimensions so the route avoids
 *    low bridges and posted weight limits.
 */

import { MAPBOX_TOKEN } from '../core/config';
import { t } from '../core/i18n';
import type { LngLat, DirectionsResult, Route } from '../core/types';
import { activeVehicle, type Settings } from '../core/settings';
import { bestBigRoadIndex, roadWidthProfile } from './bigRoads';

export class DirectionsError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'DirectionsError';
  }
}

export interface RouteRequest {
  origin: LngLat;
  destination: LngLat;
  /** Vehicle heading in degrees, used to bias the departure direction. */
  heading?: number | null;
  settings: Settings;
  signal?: AbortSignal;
  /** Extra road classes to exclude for this request only. */
  extraExclude?: string[];
  /**
   * Override the routing profile for this request. Used to fetch a non-traffic
   * line, which often follows the big roads the traffic profile detours off.
   */
  forceProfile?: 'driving' | 'driving-traffic';
  /** Force alternatives on/off for this request. */
  forceAlternatives?: boolean;
  /**
   * Optional intermediate waypoint - used by the fuel detour. Routing THROUGH
   * the station rather than TO it means the trip continues to the original
   * destination on its own once the waypoint is passed, so "return to the
   * normal route when we leave the PTT" needs no extra state to unwind.
   */
  /**
   * Intermediate waypoints. Always a list, never a bare point: `LngLat` is
   * itself a tuple, so `Array.isArray` cannot tell one point from many.
   */
  via?: LngLat[];
}

/** Human-readable messages for the API's error codes. */
const CODE_MESSAGES: Record<string, string> = {
  NoRoute: 'No drivable route found. The destination may be unreachable for this vehicle.',
  NoSegment: 'Could not find a road near the start or destination. Try moving the pin.',
  ProfileNotFound: 'That routing profile is unavailable.',
  InvalidInput: 'The route request was rejected as invalid.',
  Forbidden: 'Mapbox rejected the token. Check VITE_MAPBOX_TOKEN and its URL restrictions.',
};

function buildExcludes(s: Settings): string[] {
  const ex: string[] = [];
  if (s.route.avoidTolls) ex.push('toll');
  // With a transponder the queue is the cost, not the fee - so keep the toll
  // roads and drop only the plazas we would have to stop and pay cash at.
  else if (activeVehicle(s).easyPass) ex.push('cash_only_tolls');
  if (s.route.avoidMotorways) ex.push('motorway');
  if (s.route.avoidFerries) ex.push('ferry');
  if (s.route.avoidUnpaved) ex.push('unpaved');
  return ex;
}

function buildParams(req: RouteRequest, profile: string): URLSearchParams {
  const { settings } = req;
  const v = activeVehicle(settings);

  const p = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    geometries: 'geojson',
    overview: 'full',
    steps: 'true',
    banner_instructions: 'true',
    voice_instructions: 'true',
    voice_units: settings.units,
    roundabout_exits: 'true',
    // Thai instructions (banners + voice text) come straight from Mapbox.
    language: settings.language,
    alternatives: String(req.forceAlternatives ?? settings.route.alternatives),
  });

  // Congestion is only produced by the traffic profile; asking for it on plain
  // driving is rejected outright, so gate it.
  p.set(
    'annotations',
    profile === 'driving-traffic'
      ? 'congestion,duration,distance,speed,maxspeed'
      : 'duration,distance,speed,maxspeed',
  );

  const ex = [...new Set([...buildExcludes(settings), ...(req.extraExclude ?? [])])];
  if (ex.length) p.set('exclude', ex.join(','));

  // Vehicle dimensions. Only send them when they'd actually constrain routing -
  // sending a car's 1.95 m height is harmless but noisy, and for the truck these
  // are the whole point.
  if (v.kind === 'truck') {
    if (v.height) p.set('max_height', String(v.height));
    if (v.width) p.set('max_width', String(v.width));
    if (v.weight) p.set('max_weight', String(v.weight));
  }

  // Constrain departure to roughly the current heading (±60°). Every later
  // waypoint gets an empty entry, meaning "unconstrained" - the count must
  // match the number of coordinates or the API rejects the request.
  if (req.heading != null && Number.isFinite(req.heading)) {
    const rest = ';'.repeat((req.via?.length ?? 0) + 1);
    p.set('bearings', `${Math.round(req.heading)},60${rest}`);
  }

  return p;
}

async function request(req: RouteRequest, profile: string): Promise<DirectionsResult> {
  const { origin, destination } = req;
  const points = [origin, ...(req.via ?? []), destination];
  const coords = points.map((c) => `${c[0].toFixed(6)},${c[1].toFixed(6)}`).join(';');
  const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coords}?${buildParams(req, profile)}`;

  const res = await fetch(url, { signal: req.signal });
  let body: DirectionsResult & { message?: string };
  try {
    body = await res.json();
  } catch {
    throw new DirectionsError('Routing service returned an unreadable response.', 'BadResponse');
  }

  if (!res.ok || (body.code && body.code !== 'Ok')) {
    const code = body.code ?? (res.status === 401 || res.status === 403 ? 'Forbidden' : 'HttpError');
    throw new DirectionsError(CODE_MESSAGES[code] ?? body.message ?? `Routing failed (${code}).`, code);
  }
  if (!body.routes?.length) {
    throw new DirectionsError(CODE_MESSAGES.NoRoute, 'NoRoute');
  }
  return body;
}

/**
 * Fetch routes. Tries the live-traffic profile first when enabled and
 * transparently falls back to plain driving, because `driving-traffic` refuses
 * some parameter combinations and is rate-limited more aggressively.
 */
export async function fetchRoutes(req: RouteRequest): Promise<Route[]> {
  const wantTraffic = req.settings.route.useTraffic;
  // A caller can pin the profile - the big-roads sweep asks for plain `driving`
  // specifically, because that is the line that follows the trunk roads.
  const profiles = req.forceProfile
    ? [req.forceProfile]
    : wantTraffic
      ? ['driving-traffic', 'driving']
      : ['driving'];

  let lastError: unknown;
  for (const profile of profiles) {
    try {
      const result = await request(req, profile);
      return result.routes.map((r, i) => ({ ...r, _id: `${profile}-${i}-${Date.now()}` }));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      lastError = err;
      // A genuine "no route exists" won't be fixed by changing profile.
      if (err instanceof DirectionsError && (err.code === 'NoRoute' || err.code === 'Forbidden')) {
        throw err;
      }
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new DirectionsError('Routing failed.', 'Unknown');
}

/**
 * Comparative route search: alongside the default (traffic-aware, with up to
 * two Mapbox alternatives), also query a toll-free and a motorway-free variant
 * in parallel, then dedupe and rank. This is what surfaces the "actually
 * better" choices - e.g. the free road only 4 minutes slower than the tollway.
 */
/**
 * Tag reserved for the widest-roads option. Always present in the result when
 * any route was found at all - see the end of fetchRouteAlternatives().
 */
export const BIG_ROADS_TAG = 'big-roads';

/** Grid cell for path comparison, in degrees. ~550 m at Thai latitudes. */
const CELL_DEG = 0.005;
/** Share of the shorter route's cells that must be shared to call it the same road. */
const SAME_ROUTE_OVERLAP = 0.8;

function pathCells(route: Route): Set<string> {
  const out = new Set<string>();
  for (const [lng, lat] of route.geometry.coordinates) {
    out.add(`${Math.round(lng / CELL_DEG)}:${Math.round(lat / CELL_DEG)}`);
  }
  return out;
}

/** Fraction of the smaller cell set that the two paths share, 0..1. */
function pathOverlap(a: Set<string>, b: Set<string>): number {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  if (!small.size) return 0;
  let shared = 0;
  for (const c of small) if (large.has(c)) shared++;
  return shared / small.size;
}

export async function fetchRouteAlternatives(
  req: RouteRequest,
  /**
   * Corridors this driver has actually used before, from nav/learnedRoutes.ts.
   * Routing through them surfaces local knowledge the engine cannot infer.
   */
  learned: LngLat[][] = [],
): Promise<Route[]> {
  const { settings } = req;
  const variants: Array<{ tag?: string; promise: Promise<Route[]> }> = [
    { promise: fetchRoutes({ ...req, forceAlternatives: settings.route.alternatives }) },
  ];

  for (const vias of learned.slice(0, 2)) {
    variants.push({
      tag: 'usual',
      promise: fetchRoutes({ ...req, via: vias, forceAlternatives: false }),
    });
  }
  if (settings.route.alternatives) {
    if (!settings.route.avoidTolls) {
      variants.push({
        tag: 'no-tolls',
        promise: fetchRoutes({ ...req, extraExclude: ['toll'], forceAlternatives: false }),
      });
    }
    if (!settings.route.avoidMotorways) {
      variants.push({
        tag: 'no-motorway',
        promise: fetchRoutes({ ...req, extraExclude: ['motorway'], forceAlternatives: false }),
      });
    }

    /*
     * One extra request purely to widen the pool for the big-roads pick.
     *
     * Mapbox has no "prefer major roads" parameter, so a dual-carriageway
     * route cannot be requested - it can only be recognised among candidates.
     * The plain `driving` profile optimises distance-ish rather than live
     * traffic and routinely returns a different, trunk-road-hugging line that
     * `driving-traffic` never offers. It is one call, and it is what makes the
     * option genuinely available rather than "whichever of the usual three
     * happened to be widest".
     */
    variants.push({
      promise: fetchRoutes({ ...req, forceAlternatives: true, forceProfile: 'driving' }),
    });
  }

  const results = await Promise.allSettled(variants.map((v) => v.promise));
  const routes: Route[] = [];
  results.forEach((r, i) => {
    if (r.status !== 'fulfilled') return;
    for (const route of r.value) {
      routes.push(variants[i].tag ? { ...route, _tag: variants[i].tag } : route);
    }
  });

  // The primary request failing entirely is a real error worth surfacing.
  if (!routes.length) {
    const first = results[0];
    if (first.status === 'rejected') throw first.reason;
    throw new DirectionsError(CODE_MESSAGES.NoRoute, 'NoRoute');
  }

  /*
   * Dedupe by the PATH, not by distance and duration.
   *
   * The old test - within 1.5% on distance and 3% on duration - misses the
   * case it exists for. Two requests that return the same road can disagree on
   * duration by more than 3% simply because one asked the traffic profile and
   * one did not, so both survived. Reported from the road as "i get 4 options
   * but only 2 differences on the map": four entries, two of which were the
   * border route and two of which were the same 304/24 corridor, and no way to
   * tell which was which without driving them.
   *
   * Two routes that run over the same ground ARE the same route however their
   * estimates differ, so compare the ground: bin the coordinates into coarse
   * cells and measure how much of the smaller route the larger one covers.
   */
  const unique: Route[] = [];
  const cells: Array<Set<string>> = [];
  for (const route of routes) {
    const mine = pathCells(route);
    let dupAt = -1;
    for (let i = 0; i < unique.length; i++) {
      if (pathOverlap(cells[i], mine) >= SAME_ROUTE_OVERLAP) {
        dupAt = i;
        break;
      }
    }
    if (dupAt < 0) {
      unique.push(route);
      cells.push(mine);
      continue;
    }
    // Same road: keep the better-informed copy. A traffic-profile duration is
    // the honest one, and a tag we would otherwise lose is worth carrying over.
    const kept = unique[dupAt];
    if (!kept._tag && route._tag) kept._tag = route._tag;
    if (route.duration < kept.duration) {
      const tag = kept._tag ?? route._tag;
      unique[dupAt] = { ...route, _tag: tag };
      cells[dupAt] = mine;
    }
  }

  unique.sort((a, b) => a.duration - b.duration);
  // The quickest option is labelled for what it is, not for whichever variant
  // request happened to return the same road first. Merging by path means a
  // duplicate's tag can land on it - "NO TOLLS" on the fastest route is true
  // but not the useful thing to say about it.
  if (unique.length) unique[0]._tag = 'fastest';

  /*
   * Guarantee the big-roads option.
   *
   * "i want the routes to have at least one, if possible to have dual lanes
   * minimum all the way or most of the way, as a route option always." So the
   * widest-road candidate is tagged and given a reserved slot, rather than
   * being cut by the by-duration trim - which is exactly what would drop it,
   * since staying on big roads is often the slower choice.
   */
  const profiles = unique.map(roadWidthProfile);
  const bigIdx = bestBigRoadIndex(profiles);
  if (bigIdx >= 0) {
    const big = unique[bigIdx];
    // `_widest` rather than overwriting `_tag`: the widest route is often also
    // the fastest, and replacing the label would hide one fact to show the
    // other. The badge is rendered from this flag, so both can appear.
    big._widest = true;
    if (!big._tag) big._tag = BIG_ROADS_TAG;
  }

  const trimmed = unique.slice(0, 4);
  if (bigIdx >= 0 && !trimmed.includes(unique[bigIdx])) {
    // Drop the slowest un-tagged option to make room rather than growing the
    // list - four is already as many as anyone reads at a junction.
    const sacrifice = [...trimmed].reverse().find((r) => !r._tag);
    const at = sacrifice ? trimmed.indexOf(sacrifice) : trimmed.length - 1;
    trimmed.splice(at, 1, unique[bigIdx]);
  }
  return trimmed;
}

/** Label an alternative route for the route-choice sheet. */
export function summariseRoute(route: Route): string {
  const names = route.legs
    .flatMap((l) => l.summary.split(',').map((s) => s.trim()))
    .filter(Boolean);
  const unique = [...new Set(names)];
  return unique.slice(0, 2).join(' / ') || t('preview.alternative');
}
